import React, { ReactNode, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { TSize } from '../../tokens/types';
import HBIconButton from '../HBIconButton/HBIconButton';
import {
    ExclamationMarkTriangle24,
    InfoCircle16,
    IconProps
} from '@honeybook/icons';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import spacing from '../../styles/spacing';
import { mediumUp } from '../../styles/breakpoints';
import { TTextType } from '../HBText/HBText';
import HBText from '../HBText/HBText';
import HBLoadingSkeleton, {
    HBLoadingSkeletonProps
} from '../HBLoadingSkeleton/HBLoadingSkeleton';
import HBLinkButton, { HBLinkButtonProps } from '../HBLinkButton/HBLinkButton';
import HBTooltip from '../HBTooltip/HBTooltip';
import HBClickable from '../HBClickable/HBClickable';

// Config - Card sizes
type CardSize = Exclude<TSize, 'xl'>;

interface ICardSizeAttributes {
    headerTextType: TTextType;
    headerHeight: string;
    headerMarginBlock: string;
    headerTopRowGap: string;
    contentMarginBlock: string;
    paddingInline: string;
    iconSize: string;
    titleVerticalPadding: string;
    titleSuffixGap: string;
}

const sizes: Record<CardSize, ICardSizeAttributes> = {
    sm: {
        headerTextType: 'title16',
        headerHeight: '30px',
        headerTopRowGap: '8px',
        headerMarginBlock: '12px',
        contentMarginBlock: '16px',
        paddingInline: '16px',
        iconSize: '24px',
        titleVerticalPadding: '8px',
        titleSuffixGap: spacing['4']
    },
    md: {
        headerTextType: 'title18',
        headerTopRowGap: '8px',
        headerHeight: '36px',
        headerMarginBlock: '16px',
        contentMarginBlock: '24px',
        paddingInline: '24px',
        iconSize: '24px',
        titleVerticalPadding: '8px',
        titleSuffixGap: spacing['4']
    },
    lg: {
        headerTextType: 'title20',
        headerHeight: '42px',
        headerTopRowGap: '12px',
        headerMarginBlock: '20px',
        contentMarginBlock: '32px',
        paddingInline: '32px',
        iconSize: '42px',
        titleVerticalPadding: '12px',
        titleSuffixGap: spacing['8']
    }
};

interface IHeaderIconButtonProps {
    onClick: () => void;
    icon: (props: IconProps) => JSX.Element;
    label: string;
}

interface HeaderTitleProps {
    title: ReactNode;
    size: CardSize;
    headerIcon?: JSX.Element;
    tooltipText?: string;
    onHeaderTitleClick?: () => void;
    testIdName: string;
    titleSuffix?: ReactNode;
}

export interface HBCardProps {
    /** Title displayed in the card header. When omitted, the header is not rendered (unless `withHeader` is explicitly set). */
    title?: ReactNode;
    /** Secondary text rendered below the title in the header. */
    subtitle?: ReactNode;
    /** Controls all internal spacing, typography, and icon dimensions via a size-driven config. */
    size?: CardSize;
    /** Icon element rendered to the left of the title; SVG fill is forced to text.primary. */
    headerIcon?: JSX.Element;
    /** CSS class forwarded to the root card element. */
    className?: string;
    /** Polymorphic header action: 'custom' renders arbitrary content, 'link-button' renders an HBLinkButton, 'icon-button' renders an HBIconButton. Custom actions use column layout on mobile. */
    headerAction?:
        | { type: 'custom'; content: ReactNode }
        | {
              type: 'link-button';
              linkText: string;
              linkButtonProps: HBLinkButtonProps &
                  MustIncludeOneOfBasicTestIdWithIdProps;
          }
        | { type: 'icon-button'; iconButtonProps: IHeaderIconButtonProps };
    /** Renders a full-width horizontal divider between header and content, extending edge-to-edge via negative margin. */
    withDivider?: boolean;
    /** Explicitly controls header visibility; defaults to true when `title` is provided. Header only renders when both this and `title` are truthy. */
    withHeader?: boolean;
    /** Text shown in an info tooltip icon next to the title. */
    tooltipText?: string;
    /** Content rendered inline after the title, separated by a size-dependent gap. */
    titleSuffix?: ReactNode;
    /** Makes the title clickable with an underline hover effect; wraps it in HBClickable. */
    onHeaderTitleClick?: () => void;
    /** Adds a subtle box-shadow to the card container. Enabled by default. */
    withBoxShadow?: boolean;
    /** Content rendered inside the card body. */
    children: ReactNode;
    /** Replaces children with a loading skeleton or error state. When 'loading', renders HBLoadingSkeleton; when 'error', shows an error message with icon. */
    loadingProps?: {
        loadingState: 'loading' | 'error';
        skeletonProps?: HBLoadingSkeletonProps;
        errorMessage?: string;
    };
}

function HeaderAction({
    headerAction,
    size,
    testIdName
}: Pick<
    HBCardProps & MustIncludeOneOfBasicTestIdWithIdProps,
    'headerAction' | 'size' | 'testIdName'
>) {
    if (!headerAction) {
        return null;
    }

    if (headerAction.type === 'link-button') {
        return (
            <HBLinkButton
                {...{
                    ...headerAction.linkButtonProps,
                    testIdName: `${testIdName}-header-link-button`,
                    size: size
                }}
            >
                {headerAction.linkText}
            </HBLinkButton>
        );
    }

    if (headerAction.type === 'icon-button') {
        return (
            <HBIconButton
                {...headerAction.iconButtonProps}
                size={size}
                testIdName={`${testIdName}-header-icon-button`}
            />
        );
    }

    return (
        <HeaderActionContainer>{headerAction.content}</HeaderActionContainer>
    );
}

function ErrorState({ errorMessage }: { errorMessage?: string }) {
    return (
        <ErrorStateContainer>
            <ExclamationMarkTriangle24 color={vars.colors.text.secondary} />
            <ErrorStateText>
                <HBText type="body14" color="text.secondary">
                    {errorMessage || 'Something went wrong.'}
                </HBText>
            </ErrorStateText>
        </ErrorStateContainer>
    );
}

function HeaderTitle({
    title,
    size,
    headerIcon,
    tooltipText,
    testIdName,
    onHeaderTitleClick,
    titleSuffix
}: HeaderTitleProps) {
    const titleElement = onHeaderTitleClick ? (
        <ClickableTitle
            testIdName={`${testIdName}-clickable-card-header-title'`}
            onClick={onHeaderTitleClick}
        >
            <Title type={sizes[size].headerTextType}>{title}</Title>
        </ClickableTitle>
    ) : (
        <Title type={sizes[size].headerTextType}>{title}</Title>
    );

    return (
        <TitleAndIconContainer size={size}>
            {headerIcon && <IconWrapper>{headerIcon}</IconWrapper>}

            {titleSuffix ? (
                <TitleWithSuffix size={size}>
                    {titleElement}
                    {titleSuffix}
                </TitleWithSuffix>
            ) : (
                titleElement
            )}

            {tooltipText && (
                <HBTooltip
                    target={
                        <InfoCircleIcon color={vars.colors.text.tertiary} />
                    }
                    placement="top"
                    maxWidth="233px"
                >
                    {tooltipText}
                </HBTooltip>
            )}
        </TitleAndIconContainer>
    );
}

const HBCard = ({
    title,
    size = 'md',
    headerIcon,
    className,
    headerAction,
    withDivider = false,
    withBoxShadow = true,
    withHeader = !!title,
    testIdName,
    loadingProps,
    children,
    subtitle,
    tooltipText,
    titleSuffix,
    onHeaderTitleClick,
    id
}: PropsWithChildren<HBCardProps> & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const content = (() => {
        if (loadingProps?.loadingState === 'loading') {
            return <HBLoadingSkeleton {...loadingProps?.skeletonProps} />;
        }

        if (loadingProps?.loadingState === 'error') {
            return <ErrorState errorMessage={loadingProps?.errorMessage} />;
        }

        return children;
    })();

    const shouldRenderHeader = withHeader && !!title;

    return (
        <Card
            size={size}
            $withBoxShadow={withBoxShadow}
            className={className}
            id={id}
        >
            {shouldRenderHeader && (
                <Header size={size}>
                    <HeaderTitleAndActionContainer
                        size={size}
                        isCustomHeaderAction={headerAction?.type === 'custom'}
                    >
                        <HeaderTitle
                            testIdName={testIdName}
                            title={title}
                            size={size}
                            headerIcon={headerIcon}
                            tooltipText={tooltipText}
                            titleSuffix={titleSuffix}
                            onHeaderTitleClick={onHeaderTitleClick}
                        />

                        <HeaderAction
                            size={size}
                            headerAction={headerAction}
                            testIdName={testIdName}
                        />
                    </HeaderTitleAndActionContainer>

                    {subtitle && (
                        <HBText type="body14" color="text.secondary">
                            {subtitle}
                        </HBText>
                    )}
                </Header>
            )}
            {shouldRenderHeader && withDivider && <Divider size={size} />}
            <ContentContainer
                shouldRenderHeader={shouldRenderHeader}
                size={size}
                withDivider={withDivider}
            >
                {content}
            </ContentContainer>
        </Card>
    );
};

export default HBCard;

// -------------------------------- Styles -------------------------------- //
const Card = styled.div<{
    size: CardSize;
    $withBoxShadow: boolean;
}>`
    display: flex;
    flex-direction: column;
    background: ${vars.colors.surface.container};
    border-radius: ${vars.radius.xs};
    height: 100%;
    width: 100%;
    ${({ size }) => css`
        padding-inline: ${sizes[size].paddingInline};
    `}
    ${({ $withBoxShadow }) =>
        $withBoxShadow &&
        css`
            box-shadow: ${vars.shadow['10']};
        `};
`;

const mediumUpHeader = css`
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
`;

export const Header = styled.div<{
    size: CardSize;
}>`
    display: flex;
    flex-direction: column;
    gap: 4px;

    ${({ size }) => css`
        margin: ${sizes[size].headerMarginBlock} 0;

        ${mediumUp} {
            margin: ${sizes[size].headerMarginBlock} 0;
        }
    `}
`;

const HeaderActionContainer = styled.div`
    width: max-content;
`;

const HeaderTitleAndActionContainer = styled.div<{
    size: CardSize;
    isCustomHeaderAction: boolean;
}>`
    display: flex;
    flex-direction: column;
    /* For non-custom header actions (IE - icon-button or link-button) we don't want direction row even on mobile */
    ${({ isCustomHeaderAction }) => !isCustomHeaderAction && mediumUpHeader}

    /* Desktop */
    ${mediumUp} {
        ${mediumUpHeader}
    }

    ${({ size }) => css`
        gap: ${sizes[size].headerTopRowGap};

        ${mediumUp} {
            height: ${sizes[size].headerHeight};
        }
    `}
`;

const IconWrapper = styled.div`
    i {
        display: flex;
    }

    svg * {
        fill: ${vars.colors.text.primary};
    }
`;

const TitleAndIconContainer = styled.div<{ size: CardSize }>`
    display: flex;
    align-items: center;

    ${({ size }) => css`
        gap: ${sizes[size].headerTopRowGap};
    `}

    ${mediumUp} {
        min-width: max-content;
    }
`;

const Title = styled(HBText).attrs({
    color: 'text.primary'
})``;

const Divider = styled.div<{ size: CardSize }>`
    height: 1px;
    background-color: ${vars.colors.stroke.secondary};
    ${({ size }) => css`
        /* So divider goes 100% even though it's parent has padding */
        margin-inline: -${sizes[size].paddingInline};
    `}
`;

// The padding logic which looks a bit complex just add padding to both top and bottom in case there is a divider
// and only to the bottom if there isn't a divider
const ContentContainer = styled.div<{
    size: CardSize;
    withDivider: boolean;
    shouldRenderHeader: boolean;
}>`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    ${({ size, withDivider, shouldRenderHeader }) => {
        const { contentMarginBlock } = sizes[size];
        return css`
            margin-top: ${withDivider || !shouldRenderHeader
                ? contentMarginBlock
                : 0};
            margin-bottom: ${contentMarginBlock};
        `;
    }}
`;

const ErrorStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    justify-content: center;
`;

const ErrorStateText = styled.div`
    width: 100%;
    text-align: center;
`;

export const FullWidthCardContainer = styled.div<{ marginBottom?: string }>`
    margin-right: auto;
    margin-left: auto;
    width: 90%;

    ${mediumUp} {
        margin-right: initial;
        margin-left: initial;
        width: initial;
    }

    ${({ marginBottom }) => marginBottom && `margin-bottom: ${marginBottom};`}
`;

const InfoCircleIcon = styled(InfoCircle16)`
    display: flex;
`;

const ClickableTitle = styled(HBClickable)`
    &:hover {
        text-decoration: underline;
    }
`;

const TitleWithSuffix = styled.div<{ size: CardSize }>`
    display: flex;
    align-items: center;
    gap: ${({ size }) => sizes[size].titleSuffixGap};
`;
